import { useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { platforms, guideArticles, faqs, guideContact } from "@/mocks/guide";

export default function Guide() {
  const [openId, setOpenId] = useState<string | null>(guideArticles[0]?.id ?? null);

  return (
    <div className="space-y-6">
      <title>甜蜜惩罚观看指南与剧情解析 - 甜蜜惩罚动漫</title>
      <meta
        name="description"
        content="甜蜜惩罚观看指南：整理正版授权观看平台推荐、观看顺序建议与结局解析等深度文章，并附常见问题解答，帮助你更顺畅地追番。"
      />
      <meta name="keywords" content="甜蜜惩罚观看指南,甜蜜惩罚在哪看,甜蜜惩罚解析,甜蜜惩罚顺序" />
      <link rel="canonical" href="https://cloudschoolserver.cn/guide" />

      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "观看指南" }]} />

      <header className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
        <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">观看指南</h1>
        <p className="text-sm md:text-base text-foreground-600 leading-loose font-label max-w-3xl">
          本站不提供播放与下载功能。这里整理了《甜蜜惩罚》的正版授权观看平台、观看顺序建议，
          以及若干篇剧情解析文章，帮助你更顺畅地追完这部作品。
        </p>
      </header>

      <section>
        <h2 className="font-heading text-xl text-foreground-950 mb-4 flex items-center gap-2">
          <i className="ri-play-circle-line text-primary-600"></i>正版观看平台推荐
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {platforms.map((platform) => (
            <div
              key={platform.id}
              className="rounded-lg bg-background-50 border border-background-200 p-5 flex flex-col"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="font-heading text-base text-foreground-950">{platform.name}</h3>
                  <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 text-[11px] font-label">
                    {platform.type} · {platform.region}
                  </span>
                </div>
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700 text-xl">
                  <i className="ri-play-fill"></i>
                </span>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed font-label mb-4 flex-1">{platform.note}</p>
              <a
                href={platform.url}
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-2 w-fit px-4 py-2 rounded-md bg-background-100 border border-background-300 text-sm font-label text-foreground-800 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                前往平台 <i className="ri-external-link-line"></i>
              </a>
            </div>
          ))}
        </div>
        <p className="text-xs text-foreground-500 font-label mt-3">
          以上平台链接均为官方站点入口，本站不参与其内容运营，具体片源以平台实际提供为准。
        </p>
      </section>

      <section>
        <h2 className="font-heading text-xl text-foreground-950 mb-4 flex items-center gap-2">
          <i className="ri-article-line text-primary-600"></i>观看技巧与剧情解析
        </h2>
        <div className="space-y-3">
          {guideArticles.map((article) => {
            const open = openId === article.id;
            return (
              <div key={article.id} className="rounded-lg bg-background-50 border border-background-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : article.id)}
                  className="w-full flex items-start gap-4 p-5 text-left cursor-pointer hover:bg-background-100 transition-colors"
                >
                  <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-800 text-lg">
                    <i className="ri-lightbulb-line"></i>
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-heading text-base text-foreground-950">{article.title}</span>
                    <span className="block text-sm text-foreground-500 font-label mt-1 line-clamp-2">{article.summary}</span>
                    <span className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-foreground-400 font-label">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i> {article.publishDate}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-price-tag-3-line"></i> {article.tags.join(" · ")}
                      </span>
                    </span>
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-xl text-foreground-400 transition-transform ${
                      open ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>

                {open && (
                  <div className="px-5 pb-5 pl-[72px] animate-fade-up">
                    <div className="border-t border-background-200 pt-4 space-y-3">
                      {article.content.map((paragraph, index) => (
                        <p key={index} className="text-sm text-foreground-600 leading-loose font-label">
                          {paragraph}
                        </p>
                      ))}
                      <div className="flex flex-wrap gap-3 pt-1">
                        {article.relatedEpisodeId && (
                          <Link
                            to={`/episode/${article.relatedEpisodeId}`}
                            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-primary-100 text-primary-800 text-sm font-label hover:bg-primary-200 transition-colors cursor-pointer"
                          >
                            <i className="ri-film-line"></i> 查看相关剧情
                          </Link>
                        )}
                        {article.relatedCharacterId && (
                          <Link
                            to={`/character/${article.relatedCharacterId}`}
                            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-secondary-100 text-secondary-900 text-sm font-label hover:bg-secondary-200 transition-colors cursor-pointer"
                          >
                            <i className="ri-user-star-line"></i> 查看相关角色
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <h2 className="font-heading text-xl text-foreground-950 mb-4 flex items-center gap-2">
            <i className="ri-question-line text-primary-600"></i>常见问题
          </h2>
          <div className="space-y-3">
            {faqs.map((faq) => (
              <div key={faq.id} className="rounded-lg bg-background-50 border border-background-200 p-5">
                <h3 className="font-heading text-base text-foreground-950 mb-2 flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-accent-600 mt-0.5"></i>
                  {faq.question}
                </h3>
                <p className="text-sm text-foreground-600 leading-loose font-label pl-6">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="rounded-lg bg-primary-50 border border-primary-100 p-6 lg:sticky lg:top-24">
            <h2 className="font-heading text-lg text-foreground-950 mb-3 flex items-center gap-2">
              <i className="ri-customer-service-2-line text-primary-600"></i>联系我们
            </h2>
            <p className="text-sm text-foreground-600 leading-relaxed font-label mb-4">
              如果你发现内容有误，或想补充某段剧情与角色资料，欢迎通过以下方式告诉我们。
            </p>
            <ul className="space-y-2.5 text-sm font-label text-foreground-700">
              <li className="flex items-center gap-2">
                <i className="ri-mail-line text-primary-600"></i> {guideContact.email}
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-music-2-line text-primary-600"></i> {guideContact.social}
              </li>
            </ul>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 mt-5 text-sm font-label text-primary-700 hover:text-primary-800 cursor-pointer"
            >
              了解本站更多 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}